/**
 * FormsLibrary — member-facing JCC Forms Library (Aug 2026 native Form
 * Builder). Populated dynamically by templates published through
 * Admin; only shows forms the signed-in member's entitlements permit
 * (server-side filtered — GET /api/forms already only returns those).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, ArrowRight } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { PageHero } from "../components/shared/Primitives";
import HandoverGate from "../components/layout/HandoverGate";
import { RichTextDisplay } from "../components/forms/RichTextDisplay";

const ANY_MEMBER_ENTITLEMENT = ["carer_free", "carer_profile", "carer_essentials", "pro_carer", "family_free", "family_circle"];

function FormsLibraryInner() {
  const [forms, setForms] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/api/forms");
        if (alive) setForms(data.forms || []);
      } catch (err) {
        if (alive) setError(formatApiError(err?.response?.data?.detail) || "Could not load the Forms Library.");
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  const groups = forms.reduce((acc, f) => {
    const cat = f.category || "Other";
    (acc[cat] = acc[cat] || []).push(f);
    return acc;
  }, {});

  return (
    <div data-testid="forms-library-page">
      <PageHero
        eyebrow="JCC Forms"
        title="Your JCC Forms Library"
        description="Complete forms online, save multiple copies, and print blank or completed versions — only forms included in your membership appear here."
      />
      <section className="section pt-8 pb-24">
        <div className="container-jcc max-w-4xl">
          {!loaded ? (
            <p className="text-ink-muted text-sm py-10 text-center" data-testid="forms-library-loading">Loading forms…</p>
          ) : error ? (
            <p className="text-red-600 text-sm py-10 text-center" data-testid="forms-library-error">{error}</p>
          ) : forms.length === 0 ? (
            <p className="text-ink-muted text-sm py-10 text-center border border-dashed border-slate-200 rounded-xl" data-testid="forms-library-empty">
              No forms are available for your membership yet.
            </p>
          ) : (
            <div className="space-y-8" data-testid="forms-library-list">
              {Object.entries(groups).map(([cat, items]) => (
                <div key={cat}>
                  <h2 className="font-display text-lg font-semibold mb-3">{cat}</h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {items.map((f) => (
                      <Link key={f.template_id} to={`/forms/${f.template_id}`}
                            className="group card-base p-5 flex flex-col hover:border-teal hover:-translate-y-0.5 transition-all duration-200"
                            data-testid={`forms-library-card-${f.template_id}`}>
                        <div className="w-10 h-10 rounded-xl bg-teal/10 text-teal flex items-center justify-center">
                          <FileText className="w-5 h-5" strokeWidth={1.75} />
                        </div>
                        <h3 className="mt-3 font-display font-semibold">{f.title}</h3>
                        <RichTextDisplay content={f.description} className="mt-1.5 text-ink-muted flex-1" testId={`forms-library-card-description-${f.template_id}`} />
                        <span className="mt-3 inline-flex items-center gap-1.5 text-teal font-semibold text-sm">
                          Open form <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default function FormsLibrary() {
  return (
    <HandoverGate requiredEntitlement={ANY_MEMBER_ENTITLEMENT}>
      <FormsLibraryInner />
    </HandoverGate>
  );
}
